import { useEffect, useRef } from "react";
import type { KeyboardEvent } from "react";
import { Button } from "../components/Button/Button";
import styles from "./IdleWarningDialog.module.css";

const TITLE_ID = "idle-warning-title";

/**
 * Shown shortly before an idle sign-out (AUTH-008). Not a native <dialog>: the codebase has no
 * modal precedent yet, so this stays a plain, fully-scripted overlay — role="alertdialog" with a
 * one-element focus trap (there is only ever one control) and focus restored on close.
 */
export function IdleWarningDialog({ onStaySignedIn }: { onStaySignedIn: () => void }) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    buttonRef.current?.focus();
    return () => {
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, []);

  function keepFocusInDialog(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Tab") {
      event.preventDefault();
      buttonRef.current?.focus();
    }
  }

  return (
    <div className={styles.overlay}>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        className={styles.panel}
        onKeyDown={keepFocusInDialog}
      >
        <h2 id={TITLE_ID} className={styles.title}>
          You're about to be signed out
        </h2>
        <p className={styles.body}>
          You've been inactive for a while. For your security, ThyroAID will sign you out soon.
        </p>
        <Button ref={buttonRef} onClick={onStaySignedIn}>
          Stay signed in
        </Button>
      </div>
    </div>
  );
}
